import { InPlaceEditor } from '@syncfusion/ej2-inplace-editor';
import { FormValidator } from '@syncfusion/ej2-inputs';

import { routes } from '@/shared/routes';
import { EditableClientProps, EditableRequestBody } from '@/shared/types/editable';
import { shiftToUtc } from '@/shared/utils/date';
import { isEmptyArrayOrNull, isEmptyStringOrNull } from '@/shared/utils/misc';
import { merge, omit } from '@/shared/utils/object';
import { StatusOptions } from '@/shared/utils/status';

import { Control, ControlValue, isSwitch } from '@/front/editable/control';
import { POST } from '@/front/fetch';
import { lockSavedForm, onFormSave } from '@/front/formSave';
import { alertModal } from '@/front/modal';
import { hideOffcanvas } from '@/front/offcanvas';
import { isElementVisible } from '@/front/utils/dom';

const validators: Record<string, FormValidator> = {};

const editorsById: Record<string, InPlaceEditor> = {};
const controlsById: Record<string, Control> = {};

const propsById: Record<string, EditableClientProps> = {};
const fieldsById: Record<string, string> = {};

export const setEditor = (id: string, editor: InPlaceEditor) => (editorsById[id] = editor);
export const getEditor = (id: string) => editorsById[id];

export const setControl = (id: string, control: Control) => (controlsById[id] = control);
export const getControl = (id: string) => controlsById[id];

export const setProps = (id: string, props: EditableClientProps) => (propsById[id] = props);
export const getProps = (id: string) => propsById[id];

export const setField = (id: string, value: string) => (fieldsById[id] = value);
export const getField = (id: string) => fieldsById[id];

export const findEditorIdByField = (field: string, modelId: string, modelName: string): string | undefined => {
  return Object.keys(propsById).find(
    (editorId) =>
      propsById[editorId].field === field &&
      propsById[editorId].modelId === modelId &&
      propsById[editorId].modelName === modelName,
  );
};

export const addValidation = (p: EditableClientProps, e: HTMLElement) => {
  const form = e.closest('form');

  if (form?.id) {
    let validator = validators[form.id];

    if (!validator) {
      validator = new FormValidator(form);

      validators[form.id] = validator;

      const validate = () => {
        let valid = validator.validate();

        // Disregard errors for hidden fields
        if (!valid) {
          const errorRules = (validator as any).errorRules as { name: string }[];

          const allErrorFieldsInvisible = !errorRules.some((rule) => {
            const editable = form.querySelector(`[data-field="${rule.name}"]`) as HTMLDivElement | null;
            if (editable) return isElementVisible(editable);
            const input = form.querySelector(`input[name="${rule.name}"]`) as HTMLInputElement | null;
            if (input) return isElementVisible(input);
            return true;
          });

          if (allErrorFieldsInvisible) {
            valid = true;
          }
        }

        return valid;
      };

      // HACK: What if no controls have validation - this won't get called?
      // TODO: Move this logic to form component?
      onFormSave(form, async () => {
        form.querySelectorAll('.server-error').forEach((el) => el.remove());

        if (validate()) {
          const data = await getFormData(form);

          let body: Record<string, ControlValue> | FormData = data;

          const fileInputs = [...form.querySelectorAll<HTMLInputElement>('input[type=file]')];

          // POST as multipart rather than JSON if form contains a file
          if (fileInputs.length) {
            body = new FormData();

            body.append('data', JSON.stringify(omit(data, ...fileInputs.map((input) => input.name))));

            for (const fileInput of fileInputs) {
              body.append(fileInput.name, data[fileInput.name] as File);
            }
          }

          const res = await POST(form.action, body, { json: !fileInputs.length });

          await handleFormPostResponse(form, res);
        }
      });
    }

    if (p.validation) {
      validator.addRules(p.field, p.validation);
    }
  }
};

export const handleFormPostResponse = async (form: HTMLFormElement, res: Response) => {
  let options: StatusOptions | undefined;

  try {
    options = await res.json();
  } catch {
    // No options - ignore
  }

  if (options?.fieldErrors) {
    for (const [field, message] of Object.entries(options.fieldErrors)) {
      const editable = form.querySelector(`[data-field="${field}"]`);
      if (editable) {
        let errorSpan = editable.parentElement?.querySelector('.server-error') as HTMLSpanElement | null;
        if (!errorSpan) {
          errorSpan = document.createElement('span');
          errorSpan.className = 'text-danger small d-block mt-1 server-error';
          editable.parentElement?.appendChild(errorSpan);
        }
        errorSpan.textContent = message;
      }
    }
  } else if (options?.message) {
    alertModal(options.message);
  }

  if (res.ok) {
    delete form.dataset.dirty;
    lockSavedForm(form, !!options?.redirectUrl);

    if (options?.redirectUrl) {
      window.location.href = options.redirectUrl;
    } else {
      // HACK: Assumes we're in offcanvas - might not be?
      // HACK: Assume there are grids = might not be?
      // NOTE: Needed for refreshing after task copied as onHide()
      // refreshes row that was copied but we need to show the new
      // row as well
      hideOffcanvas({ refreshGrids: true });
    }
  }
};

export const getFormData = async (form: HTMLFormElement) => {
  let data: Record<string, ControlValue> = Object.keys(controlsById)
    .filter((id) => form.contains(controlsById[id].element))
    .map((id) => {
      const control = controlsById[id];
      const props = propsById[id];

      if (props.save === false) {
        return {};
      }

      let value: ControlValue;
      let enabled: boolean;

      if (isSwitch(control)) {
        value = maybeNegate(props, control.checked);
        enabled = !control.disabled;
      } else {
        value = control.value;
        enabled = control.enabled;
      }

      if (!enabled) {
        return {};
      }

      const { type, returnType, defaultValue } = propsById[id];

      if (typeof value === 'string') {
        value = value.trim() || null;
      }

      if ((isEmptyStringOrNull(value) || isEmptyArrayOrNull(value)) && defaultValue !== undefined) {
        value = defaultValue;
      } else if (type === 'Integer') {
        value = parseInt(value as string);

        if (isNaN(value)) {
          value = undefined;
        }
      } else if (type === 'Float') {
        value = parseFloat(value as string);

        if (isNaN(value)) {
          value = undefined;
        }
      } else if (type === 'Date') {
        if (value) {
          value = shiftToUtc(value as Date);
        }
      }

      if (returnType === 'string') {
        value = value?.toString();
      }

      return {
        [fieldsById[id]]: value,
      };
    })
    .reduce(merge);

  // Add any additional input values in form (excluding within editables)

  const inputs = [...form.querySelectorAll('input,textarea,select')]
    .map((element) => element as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement)
    .filter(
      (input) =>
        input.name &&
        !Object.values(fieldsById).includes(input.name) &&
        (input.dataset.save ?? 'true').toLowerCase() !== 'false',
    );

  for (const input of inputs) {
    const isArray = input.name.endsWith('[]');

    const name = isArray ? input.name.substring(0, input.name.length - 2) : input.name;

    let value: string | boolean | File | undefined = input.value;

    if (['checkbox', 'radio'].includes(input.type)) {
      const checked = (input as HTMLInputElement).checked;

      if (input.type === 'checkbox') {
        value = checked;
      } else {
        value = checked ? input.value : undefined;
      }
    } else if (input.type === 'file') {
      const file = (input as HTMLInputElement).files?.[0];

      if (file) {
        value = file;
      }
    }

    if (value !== undefined) {
      data = merge(data, {
        [name]: isArray ? [...((data[name] ?? []) as string[]), value] : value,
      });
    }
  }

  return data;
};

export const updateEditor = async (id: string, value: any) => {
  const element = editorsById[id].getRootElement();

  editorsById[id].value = value;

  element.setAttribute('data-empty', isEmptyStringOrNull(value) || isEmptyArrayOrNull(value) ? 'true' : 'false');

  const { modelId, modelName, field } = propsById[id];

  const url = routes.data.editable({ model: modelName });

  const body: EditableRequestBody = {
    params: {
      name: field,
      primaryKey: modelId,
      value,
    },
  };

  await POST(url, body);
};

export const setEditableValue = async (id: string, value: any) => {
  const editor = editorsById[id];

  if (editor) {
    await updateEditor(id, value);
  }
};

export const maybeNegate = (p: EditableClientProps, value?: boolean) => !p.negate === !!value;
